/* eslint-disable camelcase */
/**
 * An abstraction for sending analytics data.
 * If instrumenting new events for GTM, first check this reference
 * https://developers.google.com/tag-platform/gtagjs/reference/events#login
 * To see if an OOB recommended event exists.
 */

import { parseCookies } from 'nookies';
import isBrowser from './is-browser';
const PAGE_VIEW = 'virtual_page_view';

export const pushEvent = (...args) => {
  if (isBrowser && window.dataLayer) {
    window.dataLayer.push(...args);
    return true;
  }
  return false;
};

export const sendEvent = (eventLabel, data = {}) => {
  if (isBrowser && typeof data === 'object') {
    const cookies = parseCookies();
    // set user_id
    if (cookies?.__uid) data.user_id = cookies.__uid;
  }

  return pushEvent({
    event: eventLabel,
    ...data,
  });
};

/**
 * @typedef {Object} EcommerceItem
 * @property {string} item_name - Name of the item.
 * @property {string} item_id - Unique ID or SKU of the item.
 * @property {string} [price] - Price per unit.
 * @property {number} discount -  item.
 * @property {number} quantity - Quantity of the item.
 * @property {string} [item_brand] - Brand of the item.
 * @property {string} [item_category] - Primary category of the item.
 * @property {string} [item_category2] - Secondary category of the item.
 * @property {string} [item_list_id] - The ID of the list in which the item was presented to the user.
 * @property {string} [item_list_name] - The name of the list in which the item was presented to the user.
 * @property {string} [location_id] - The ID of the location.
 * @property {string} [item_variant] - Variant of the item (e.g., color or size).
 */

/**
 * @function sendEcommerceEvent
 * @param {'purchase'|'add_to_cart'|'view_item'|'view_promotion'} eventLabel - The GA4 event name
 * @param {Object} ecommerceData - The ecommerce object containing relevant event details.
 * @param {string} [ecommerceData.transaction_id] - Unique transaction ID (for purchase events).
 * @param {string} [ecommerceData.affiliation] - Store or sales affiliation.
 * @param {number} [ecommerceData.value] - Total revenue value.
 * @param {string} [ecommerceData.currency] - ISO 4217 currency code (e.g., 'USD').
 * @param {number} [ecommerceData.tax] - Tax amount.
 * @param {number} [ecommerceData.shipping] - Shipping cost.
 * @param {string} [ecommerceData.promotion_id] - The promotion id.
 * @param {string} [ecommerceData.promotion_name] - The promotion name.
 * @param {Array<EcommerceItem>} ecommerceData.items - List of product items involved in the event.
 * @returns
 */
export const sendEcommerceEvent = (eventLabel, ecommerceData) => {
  pushEvent({ ecommerce: null });
  return sendEvent(eventLabel, { ecommerce: ecommerceData });
};

export const sendPageView = async (pageTitle, data = {}) => {
  return sendEvent(PAGE_VIEW, {
    virtual_page_title: pageTitle,
    ...data,
  });
};

export const setMetricsData = (data) => sendEvent('set_data', data);

export const sendSearchMetricData = (searchTerm = '') =>
  sendEvent('search', { search_term: searchTerm });

export const sendLinkClickEvent = (parentId, linkId, linkText, url) =>
  sendEvent('link_click', {
    parent_id: parentId,
    link_id: linkId,
    link_text: linkText,
    url,
  });

export const sendMenuClickEvent = (item_id) =>
  sendEvent('menu_click', { item_id });

export const sendDrawerClickEvent = () => sendEvent('open_discount_drawer');
